
import { Component, OnInit, AfterViewInit, ViewChild, ElementRef } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { UntypedFormBuilder, Validators, UntypedFormGroup } from '@angular/forms';
import { NgbModal, NgbDateStruct } from '@ng-bootstrap/ng-bootstrap';
import { MatchValidator } from '../../components/shared/match.validator';
import { MinAgeValidator } from '../../components/shared/min-age.validator';
import { MaxAgeValidator } from '../../components/shared/max-age.validator';
import { ForbiddenValueValidator } from '../../components/shared/forbidden-value.validator';
import { InputBoxComponent } from '../../components/shared/input-box/input-box.component';
import { DuplicateSamplePopupComponent } from '../../components/duplicate-sample-popup/duplicate-sample-popup.component';
import { AgeService } from '../../services/age.service';
import { UserService } from '../../services/user.service';
import { AgencyService } from '../../services/agency.service';
import { SuffixService } from '../../services/suffix.service';
import { GenderService } from '../../services/gender.service';
import { IGender } from '../../interfaces/gender';
import { RaceService } from '../../services/race.service';
import { IRace } from '../../interfaces/race';
import { SampleSourceService } from '../../services/sample-source.service';
import { ISampleSource } from '../../interfaces/sample-source';
import { QualifyingOffenseService } from '../../services/qualifying-offense.service';
import { IQualifyingOffense } from '../../interfaces/qualifying-offense';
import { StateService } from '../../services/state.service';
import { IState } from '../../interfaces/state';
import { PreLogService } from '../../services/pre-log.service';
import { IPreLog } from '../../interfaces/pre-log';
import { DNASampleService } from '../../services/dna-sample.service';
import { IDuplicateDNASampleResult } from '../../interfaces/duplicate-dna-sample-result';
import { Permission } from '../../interfaces/permission.enum';
import { VACORISSampleService } from '../../services/va-coris-sample.service';
import { ConfigurationService } from '../../services/configuration.service';
import { SourceId } from '../../interfaces/source-id.enum';
import { ErrorService } from '../../services/error.service';
import { IExternalLinks } from '../../interfaces/external-links';
import { IConfiguration } from '../../interfaces/configuration';

@Component({
    selector: 'app-pre-log-entry',
    templateUrl: './pre-log-entry.component.html',
    styles: [],
    standalone: false
})
export class PreLogEntryComponent implements OnInit, AfterViewInit {
  @ViewChild("barcodeField") barcodeField: ElementRef = {} as ElementRef;
  @ViewChild("confirmBarcodeField") confirmBarcodeField: ElementRef = {} as ElementRef;
  @ViewChild("docNumberField") docNumberField: ElementRef = {} as ElementRef;
  @ViewChild("lastNameField") lastNameField: ElementRef = {} as ElementRef;
  @ViewChild("addAliasInputBox") addAliasInputBox: InputBoxComponent = {} as InputBoxComponent;
  @ViewChild("sidTextbox") sidTextbox: ElementRef = {} as ElementRef;
  @ViewChild("duplicateSamplePopup") duplicateSamplePopup: DuplicateSamplePopupComponent = {} as DuplicateSamplePopupComponent;

  // Data variables for binding
  public dateEntered: Date;
  public enteredBy: string;
  public agencyId: number;
  public agencyName: string;
  public suffixes: string[];
  public aliases: string[] = [];
  public genders: IGender[];
  public races: IRace[];
  public sampleSources: ISampleSource[];
  public qualifyingOffenses: IQualifyingOffense[];
  public nonModifierQualifyingOffenses: IQualifyingOffense[];
  public states: IState[];
  public newSample: IDuplicateDNASampleResult;
  public duplicateSamples: IDuplicateDNASampleResult[];
  public config: IConfiguration;
  public externalLinks: IExternalLinks;

  public preLogForm: UntypedFormGroup;

  // Variable to control dyanmic UI and validation messages
  public duplicateWorking: boolean = false;
  public barcodeWorking: boolean = false;
  public docNumberWorking: boolean = false;
  public errorMessage: string;
  public useOffenseDropdown: boolean = false;
  public showRelatedOffense: boolean = false;
  public showOffenseFreeText: boolean = false;
  public freeTextLabel: string;
  public showSORFields: boolean = false;
  public showArresteeFields: boolean = false;
  public showRemoveError: boolean = false;
  public showDuplicateError: boolean = false;
  public showAgeWarning: boolean = false;
  public minimumAge: number;
  public showValidation: boolean = false;
  public showVACORISNumber: boolean = false;
  public lastCheckedDocNumber: string = "";
  public docNumberNotFound: boolean = false;

  // FormControl accessors
  public get kitBarcode() {
    return this.preLogForm.get("kitBarcode");
  }

  public get confirmKitBarcode() {
    return this.preLogForm.get("confirmKitBarcode");
  }

  public get docNumber() {
    return this.preLogForm.get("docNumber");
  }

  public get lastName() {
    return this.preLogForm.get("lastName");
  }

  public get firstName() {
    return this.preLogForm.get("firstName");
  }

  public get middleInitial() {
    return this.preLogForm.get("middleInitial");
  }

  public get nameSuffix() {
    return this.preLogForm.get("nameSuffix");
  }

  public get aliasList() {
    return this.preLogForm.get("aliasList");
  }

  public get gender() {
    return this.preLogForm.get("gender");
  }

  public get race() {
    return this.preLogForm.get("race");
  }

  public get dateOfBirth() {
    return this.preLogForm.get("dateOfBirth");
  }

  public get ssn() {
    return this.preLogForm.get("ssn");
  }

  public get sid() {
    return this.preLogForm.get("sid");
  }

  public get reasonForCollection() {
    return this.preLogForm.get("reasonForCollection");
  }

  public get qualifyingChargeDropdown() {
    return this.preLogForm.get("qualifyingChargeDropdown");
  }

  public get qualifyingChargeTextbox() {
    return this.preLogForm.get("qualifyingChargeTextbox");
  }

  public get qualifyingChargeDropdown2() {
    return this.preLogForm.get("qualifyingChargeDropdown2");
  }

  public get qualifyingChargeFreeText() {
    return this.preLogForm.get("qualifyingChargeFreeText");
  }

  public get state() {
    return this.preLogForm.get("state");
  }

  public get arrestingOfficer() {
    return this.preLogForm.get("arrestingOfficer");
  }

  public get dcn() {
    return this.preLogForm.get("dcn");
  }

  public get collectedBy() {
    return this.preLogForm.get("collectedBy");
  }

  public get dateCollected() {
    return this.preLogForm.get("dateCollected");
  }

  // Used for minDate binding in Date Collected date picker
  public get monthsAgo3(): NgbDateStruct {
    let today: Date = new Date();
    let year: number = today.getFullYear();
    let month: number = today.getMonth() + 1;
    if (month <= 3) {
      year = year - 1;
      month = month + 9;
    } else {
      month = month - 3;
    }
    return { year: year, month: month, day: today.getDate() };
  }

  // Used for maxDate binding in date picker
  public get today(): NgbDateStruct {
    let today: Date = new Date();
    return { year: today.getFullYear(), month: today.getMonth() + 1, day: today.getDate() };
  }

  // Initialization
  constructor(
    private readonly titleService: Title,
    private readonly fb: UntypedFormBuilder,
    private readonly router: Router,
    private readonly modalService: NgbModal,
    private readonly ageService: AgeService,
    private readonly userService: UserService,
    private readonly agencyService: AgencyService,
    private readonly suffixService: SuffixService,
    private readonly genderService: GenderService,
    private readonly raceService: RaceService,
    private readonly sampleSourceService: SampleSourceService,
    private readonly qualifyingOffenseService: QualifyingOffenseService,
    private readonly stateService: StateService,
    private readonly preLogService: PreLogService,
    private readonly dnaSampleService: DNASampleService,
    private readonly vaCorisSampleService: VACORISSampleService,
    private readonly configService: ConfigurationService,
    private readonly errorService: ErrorService
  ) { }

  public ngOnInit(): void {
    this.titleService.setTitle("Pre-Log Entry");

    var currentUser = this.userService.getCurrentUser();
    this.showVACORISNumber = currentUser.permissions.includes(Permission.ViewVACORISNumber);

    let preLog: IPreLog = this.preLogService.getCurrentPreLog();

    // If we have a preLog in session that is not yet saved, then load the data (likely the user clicked Edit on the Review screen)
    if (preLog && preLog.id === 0) {
      this.dateEntered = new Date(preLog.dateEntered.year, preLog.dateEntered.month - 1, preLog.dateEntered.day)
      this.enteredBy = preLog.enteredBy;
      this.agencyId = preLog.agencyId;
      this.agencyName = preLog.agencyName;
      this.aliases = preLog.aliases;
      this.lastCheckedDocNumber = preLog.docNumber;
    } else {
      // If we have a preLog in session and it had been saved (i.e. it has an id), clear it so we start again with an empty form
      if (preLog && preLog.id > 0) {
        preLog = null;
        this.preLogService.clearCurrentPreLog();
      }

      // Set auto-populated values base on current state (date and login)
      this.dateEntered = new Date();
      this.dateEntered.setHours(0, 0, 0, 0);

      this.enteredBy = currentUser.firstName.substr(0, 1).toUpperCase() + currentUser.lastName.toUpperCase();
      this.agencyId = currentUser.agencyId;

      this.agencyService.getAgencyName(currentUser.agencyId)
        .subscribe(data => {
          this.agencyName = data;
        }, error => {
          this.errorMessage = this.errorService.getErrorMessage(error);
        });
    }

    this.config = this.configService.getConfiguration();

    this.configService.getExternalLinks()
      .subscribe(
        data => this.externalLinks = data,
        error => this.errorMessage = this.errorService.getErrorMessage(error)
      );

    // Setup form group
    this.preLogForm = this.fb.group({
      kitBarcode: [preLog ? preLog.kitBarcode : "", [Validators.required]],
      confirmKitBarcode: [preLog ? preLog.kitBarcode : ""],
      docNumber: [preLog ? preLog.docNumber : ""],
      lastName: [preLog ? preLog.lastName : "", [Validators.required]],
      firstName: [preLog ? preLog.firstName : "", [Validators.required]],
      middleInitial: [preLog && preLog.middleInitial ? preLog.middleInitial : ""],
      nameSuffix: [preLog && preLog.nameSuffix ? preLog.nameSuffix : ""],
      aliasList: [preLog && preLog.aliases ? preLog.aliases : ""],
      gender: [preLog ? preLog.genderCode : "", [Validators.required]],
      race: [preLog ? preLog.raceId : "", [Validators.required]],
      dateOfBirth: [preLog ? preLog.dateOfBirth : "", [Validators.required]],
      ssn: [preLog && preLog.ssn ? preLog.ssn : "", [Validators.pattern(/^\d{3}-\d{2}-\d{4}$/)]],
      sid: [preLog && preLog.sid ? preLog.sid : "VA"],
      reasonForCollection: [preLog ? preLog.sourceId : "", [Validators.required]],
      qualifyingChargeDropdown: [preLog && preLog.qcDropdown && preLog.qcDropdown !== 0 ? preLog.qcDropdown : ""],
      qualifyingChargeTextbox: [preLog && preLog.qcTextbox ? preLog.qcTextbox : ""],
      qualifyingChargeDropdown2: [preLog && preLog.qcDropdown2 && preLog.qcDropdown2 !== 0 ? preLog.qcDropdown2 : ""],
      qualifyingChargeFreeText: [preLog && preLog.qcFreeText ? preLog.qcFreeText : ""],
      state: [preLog && preLog.state ? preLog.state : ""],
      arrestingOfficer: [preLog && preLog.arrestingOfficer ? preLog.arrestingOfficer : ""],
      dcn: [preLog && preLog.dcn ? preLog.dcn : ""],
      collectedBy: [preLog ? preLog.collectedBy : "", [Validators.required]],
      dateCollected: [preLog ? preLog.dateCollected : this.today, [Validators.required]]
    }, {validators: [
      MatchValidator("kitBarcode", "confirmKitBarcode"),
      MaxAgeValidator("dateOfBirth", "dateCollected", 100, this.ageService)
    ]
    });

    // Populate drop-downs
    this.suffixService.getSuffixes()
      .subscribe(data => {
        this.suffixes = data;
      }, error => {
        this.errorMessage = this.errorService.getErrorMessage(error);
      });

    this.genderService.getGenders()
      .subscribe(data => {
        this.genders = data;
      }, error => {
        this.errorMessage = this.errorService.getErrorMessage(error);
      });

    this.raceService.getRaces()
      .subscribe(data => {
        this.races = data;
      }, error => {
        this.errorMessage = this.errorService.getErrorMessage(error);
      });

    this.sampleSourceService.getSampleSources()
      .subscribe(data => {
        this.sampleSources = data;
        this.onReasonChange(true, (preLog && preLog.qcDropdown) ? true : false);
      }, error => {
        this.errorMessage = this.errorService.getErrorMessage(error);
      });
  }

  // Set focus to barcode field
  public ngAfterViewInit(): void {
    this.barcodeField.nativeElement.focus();
  }

  public onBarcodeEnterKey(): void {
    this.confirmBarcodeField.nativeElement.focus();
  }

  public onConfirmBarcodeEnterKey(): void {
    if (this.showVACORISNumber)
      this.docNumberField.nativeElement.focus();
    else
      this.lastNameField.nativeElement.focus();
  }

  // Duplicate barcode check
  public checkDuplicateBarcode(): void {
    if (!this.kitBarcode.value || this.kitBarcode.invalid || this.preLogForm.hasError("match")) {
      this.confirmKitBarcode.setErrors(null);
      return;
    }

    this.barcodeWorking = true;

    this.preLogService.isDuplicateBarcode(this.kitBarcode.value.trim()).subscribe(data => {
      if (data) {
        this.confirmKitBarcode.setErrors({ "duplicateBarcode": true });
      } else {
        this.confirmKitBarcode.setErrors(null);
      }
      this.barcodeWorking = false;
    }, error => {
        this.errorMessage = this.errorService.getErrorMessage(error);
        this.barcodeWorking = false;
    })
  }

  // Lookup for VACORIS #
  public checkDocNumber(): void {
    this.docNumberNotFound = false;

    if (!this.docNumber.value || (this.docNumber.value.trim() === this.lastCheckedDocNumber))
      return;

    this.docNumberWorking = true;

    this.vaCorisSampleService.getVACORISSample(this.docNumber.value.trim()).subscribe(data => {
      this.lastName.setValue(data.lastName);
      this.firstName.setValue(data.firstName);
      this.middleInitial.setValue(data.middleInitial);
      this.nameSuffix.setValue(data.suffix);
      this.gender.setValue(data.gender);
      this.race.setValue(data.raceId);
      this.dateOfBirth.setValue(this.ConvertToNgbStruct(data.dateOfBirth));
      this.ssn.setValue(data.ssn);
      this.sid.setValue(data.sid ? "VA" + data.sid : "VA");

      this.lastCheckedDocNumber = this.docNumber.value;
      this.docNumberWorking = false;
    }, error => {
      if (error.status == 404) {
        this.docNumberNotFound = true;
        this.lastName.setValue("");
        this.firstName.setValue("");
        this.middleInitial.setValue("");
        this.nameSuffix.setValue("");
        this.gender.setValue("");
        this.race.setValue("");
        this.dateOfBirth.setValue("");
        this.ssn.setValue("");
        this.sid.setValue("VA");

        this.lastCheckedDocNumber = this.docNumber.value.trim();
      }
      else {
        this.errorMessage = this.errorService.getErrorMessage(error);
      }

      this.docNumberWorking = false;
    })
  }

  // Pre-Log tips
  public open(e: MouseEvent, content: any): void {
    e.preventDefault();
    this.modalService.open(content, { size: 'lg', ariaLabelledBy: 'modal-basic-title' });
  }

  // Add/Remove aliases
  public addAlias(): void {
    this.showRemoveError = false;
    this.showDuplicateError = false;
    this.addAliasInputBox.open();
  }

  public addAliasValue(textValue: string): void {
    if (textValue) {
      if (this.aliases.includes(textValue)) {
        this.showDuplicateError = true;
        return;
      }

      this.aliases.push(textValue);
    }
  }

  public removeAlias(): void {
    this.showRemoveError = false;
    this.showDuplicateError = false;

    var index = this.aliases.indexOf(this.aliasList.value);
    if (index !== -1) {
      this.aliases.splice(index, 1);
    } else {
      this.showRemoveError = true;
    }
  }

  public searchStartsWithString(term: string, item: string): boolean {
    return item.toLocaleLowerCase().startsWith(term.toLocaleLowerCase());
  }

  public searchStartsWithGender(term: string, item: IGender): boolean {
    return item.name.toLocaleLowerCase().startsWith(term.toLocaleLowerCase());
  }

  public searchStartsWithRace(term: string, item: IRace): boolean {
    return item.name.toLocaleLowerCase().startsWith(term.toLocaleLowerCase());
  }

  public searchStartsWithSampleSources(term: string, item: ISampleSource): boolean {
    return item.preLogName.toLocaleLowerCase().startsWith(term.toLocaleLowerCase());
  }

  public searchStartsWithQualifyingOffenses(term: string, item: IQualifyingOffense): boolean {
    return item.code.toLocaleLowerCase().startsWith(term.toLocaleLowerCase());
  }

  public searchStartsWithState(term: string, item: IState): boolean {
    return item.abbreviation.toLocaleLowerCase().startsWith(term.toLocaleLowerCase());
  }

  // Manage SID default value (place cursor after "VA" when tabbing in if only "VA" is there)
  public ssnKeyDown(e: KeyboardEvent): void {
    e.preventDefault();

    if (this.sid.value === "VA") {
      this.setInputSelection(this.sidTextbox.nativeElement, 2, 2);
    } else {
      this.setInputSelection(this.sidTextbox.nativeElement, 0, this.sid.value ? this.sid.value.length : 0);
    }
  }

  private setInputSelection(input: any, startPos: number, endPos: number): void {
    if (input.setSelectionRange) {
      input.focus();
      input.setSelectionRange(startPos, endPos);
    } else if (input.createTextRange) {
      var range = input.createTextRange();
      range.collapse(true);
      range.moveEnd('character', endPos);
      range.moveStart('character', startPos);
      range.select();
    }
  }

  // Manage dynamic UI and validation based on Reason for Collection and Qualifying Charge/Conviction
  public onReasonChange(retainValues: boolean = false, triggerChargeChange: boolean = false): void {
    let sampleSource: ISampleSource = this.sampleSources.find(x => x.id === this.reasonForCollection.value);
    if (sampleSource == null)
      return;

    this.useOffenseDropdown = sampleSource.showOffensesAsList;
    this.showRelatedOffense = false;
    this.showOffenseFreeText = false;
    if (!retainValues) {
      this.qualifyingChargeFreeText.setValue("");
    }

    if (sampleSource !== undefined && sampleSource.showOffensesAsList) {
      this.qualifyingOffenseService.getQualifyingOffenses(sampleSource.id)
        .subscribe(data => {
          this.qualifyingOffenses = data;
          if (!retainValues) {
            this.qualifyingChargeDropdown.setValue("");
          }
          if (sampleSource.allowOffenseFreeText) {
            this.qualifyingOffenses.push({ id: -1, code: sampleSource.offenseFreeTextLabel, description: "", isModifier: false })
          }
          if (triggerChargeChange) {
            this.onChargeChange(true);
          }
        }, error => {
          this.errorMessage = this.errorService.getErrorMessage(error);
        })
      this.qualifyingChargeDropdown.setValidators([Validators.required]);
      this.qualifyingChargeTextbox.setValue("");
      this.qualifyingChargeTextbox.setValidators(null);
    } else {
      if (sampleSource.qualifyingChargeRequired)
        this.qualifyingChargeTextbox.setValidators([
          Validators.required,
          ForbiddenValueValidator(this.config.allowedQualifyingOffenses, this.config.forbiddenQualifyingOffenses)
        ]);
      else
        this.qualifyingChargeTextbox.setValidators(ForbiddenValueValidator(this.config.allowedQualifyingOffenses, this.config.forbiddenQualifyingOffenses));
      this.qualifyingChargeDropdown.setValue("");
      this.qualifyingChargeDropdown.setValidators(null);
    }

    this.qualifyingChargeTextbox.updateValueAndValidity();
    this.qualifyingChargeDropdown.updateValueAndValidity();

    // If Arrestee, then show Arresting Officer and DCN and make them required
    if (sampleSource !== undefined && sampleSource.id === SourceId.Arrestee) {
      this.showArresteeFields = true;
      this.arrestingOfficer.setValidators([Validators.required]);
      this.dcn.setValidators([Validators.required, Validators.pattern(/^[A-Z0-9]{7}$/)]);
    } else {
      this.showArresteeFields = false;
      this.arrestingOfficer.setValue("");
      this.dcn.setValue("");
      this.arrestingOfficer.setValidators(null);
      this.dcn.setValidators(null);
    }

    this.arrestingOfficer.updateValueAndValidity();
    this.dcn.updateValueAndValidity();

    // If Sex Offender Registry, then show state field
    if (sampleSource !== undefined && sampleSource.id === SourceId.SexOffenderRegistry) {
      this.stateService.getStates()
        .subscribe(data => {
          this.states = data;
        }, error => {
          this.errorMessage = this.errorService.getErrorMessage(error);
        })
      this.showSORFields = true;
    } else {
      this.showSORFields = false;
      this.state.setValue("");
    }

    this.state.updateValueAndValidity();

    this.checkAge();
  }

  public onChargeChange(retainValues: boolean = false): void {
    let sampleSource: ISampleSource = this.sampleSources.find(x => x.id === this.reasonForCollection.value);
    let qualifyingCharge: IQualifyingOffense = this.qualifyingOffenses.find(x => x.id === this.qualifyingChargeDropdown.value);

    if (qualifyingCharge !== undefined && qualifyingCharge.isModifier) {
      this.nonModifierQualifyingOffenses = this.qualifyingOffenses.filter(x => !x.isModifier);
      if (!retainValues) {
        this.qualifyingChargeDropdown2.setValue("");
      }
      this.qualifyingChargeDropdown2.setValidators([Validators.required]);
      this.showRelatedOffense = true;
    } else {
      this.qualifyingChargeDropdown2.setValue("");
      this.qualifyingChargeDropdown2.setValidators(null);
      this.showRelatedOffense = false;
    }

    this.qualifyingChargeDropdown2.updateValueAndValidity();

    if (qualifyingCharge !== undefined && sampleSource !== undefined && qualifyingCharge.code === sampleSource.offenseFreeTextLabel) {
      this.freeTextLabel = sampleSource.offenseFreeTextLabel
      if (!retainValues) {
        this.qualifyingChargeFreeText.setValue("");
      }
      this.showOffenseFreeText = true;
      this.qualifyingChargeFreeText.setValidators([
        Validators.required,
        ForbiddenValueValidator(this.config.allowedQualifyingOffenses, this.config.forbiddenQualifyingOffenses)
      ]);
    } else {
      this.qualifyingChargeFreeText.setValue("");
      this.showOffenseFreeText = false;
      this.qualifyingChargeFreeText.setValidators(null);
    }

    this.qualifyingChargeFreeText.updateValueAndValidity();
  }

  // Age validation or warning (based on Reason for Collection)
  public checkAge(): void {
    let sampleSource: ISampleSource = this.sampleSources.find(x => x.id === this.reasonForCollection.value);

    this.showAgeWarning = false;
    this.preLogForm.setValidators([MatchValidator("kitBarcode", "confirmKitBarcode")]);

    if (sampleSource !== undefined && sampleSource.minimumAge) {
      this.minimumAge = sampleSource.minimumAge;
      if (sampleSource.ageIsError) {
        this.preLogForm.setValidators([
          MatchValidator("kitBarcode", "confirmKitBarcode"),
          MinAgeValidator("dateOfBirth", "dateCollected", sampleSource.minimumAge, this.ageService),
          MaxAgeValidator("dateOfBirth", "dateCollected", 100, this.ageService)
        ]);
      } else {
        this.preLogForm.setValidators([
          MatchValidator("kitBarcode", "confirmKitBarcode"),
          MaxAgeValidator("dateOfBirth", "dateCollected", 100, this.ageService)
        ]);
        this.checkAgeWarning(sampleSource.minimumAge);
      }
    }
  }

  private checkAgeWarning(minimumAge: number): void {
    if (!this.dateOfBirth.value || this.dateOfBirth.invalid ||
      !this.dateCollected.value || this.dateCollected.invalid)
      return;

    var birthDate = new Date(this.dateOfBirth.value.year, this.dateOfBirth.value.month, this.dateOfBirth.value.day);
    var calcDate = new Date(this.dateCollected.value.year, this.dateCollected.value.month, this.dateCollected.value.day);

    var age = this.ageService.computeAge(birthDate, calcDate);
    this.showAgeWarning = (age < minimumAge);
  }

  // Submission logic
  public onSubmit(): void {
    // If the form is not valid, set a flag to show validation errors
    if (this.preLogForm.invalid) {
      this.showValidation = true;
      return;
    }

    this.duplicateWorking = true;

    // Check for a possible duplicate
    this.dnaSampleService.isDuplicate(this.lastName.value, this.firstName.value, this.dateOfBirth.value, this.ssn.value, this.sid.value, this.docNumber.value)
      .subscribe(data => {
        if (data.length === 0) {
          this.continueToReview();
          this.duplicateWorking = false;
        } else {
          this.duplicateWorking = false;
          // Show duplicates
          this.newSample = this.getNewSample();
          this.duplicateSamples = data;
          this.duplicateSamplePopup.open();
        }
      }, error => {
        this.errorMessage = this.errorService.getErrorMessage(error);
      });
  }

  private getNewSample(): IDuplicateDNASampleResult {
    return {
      id: 0,
      firstName: this.firstName.value,
      middleInitial: this.middleInitial.value ? this.middleInitial.value : "",
      lastName: this.lastName.value,
      ssns: this.ssn.value ? [this.ssn.value] : [],
      sid: this.sid.value && this.sid.value !== "VA" ? this.sid.value : "",
      docNumber: this.docNumber.value ? this.docNumber.value : "",
      dateOfBirth: new Date(this.dateOfBirth.value.year, this.dateOfBirth.value.month - 1, this.dateOfBirth.value.day),
      dateCollected: new Date(this.dateCollected.value.year, this.dateCollected.value.month - 1, this.dateCollected.value.day)
    };
  }

  public onClear(): void {
    this.kitBarcode.setValue("");
    this.confirmKitBarcode.setValue("");
    this.docNumber.setValue("");
    this.lastName.setValue("");
    this.firstName.setValue("");
    this.middleInitial.setValue("");
    this.nameSuffix.setValue("");
    this.aliasList.setValue("");
    this.gender.setValue("");
    this.race.setValue("");
    this.dateOfBirth.setValue("");
    this.ssn.setValue("");
    this.sid.setValue("VA");
    this.reasonForCollection.setValue("");
    this.qualifyingChargeDropdown.setValue("");
    this.qualifyingChargeTextbox.setValue("");
    this.qualifyingChargeDropdown2.setValue("");
    this.qualifyingChargeFreeText.setValue("");
    this.state.setValue("");
    this.arrestingOfficer.setValue("");
    this.dcn.setValue("");
    this.collectedBy.setValue("");
    this.dateCollected.setValue("");

    this.preLogService.clearCurrentPreLog();

    this.lastCheckedDocNumber = "";
  }

  // Save the data and go to the next step
  public continueToReview(): void {
    var preLog = this.getPreLog();
    this.preLogService.setCurrentPreLog(preLog);
    this.router.navigate(["/pre-log/review"]);
  }

  private getPreLog(): IPreLog {
    let sampleSource: ISampleSource = this.sampleSources.find(x => x.id === this.reasonForCollection.value);
    let gender: IGender = this.genders.find(x => x.code === this.gender.value);
    let race: IRace = this.races.find(x => x.id === this.race.value);

    return {
      id: 0,
      kitBarcode: this.kitBarcode.value,
      dateEntered: {
        year: this.dateEntered.getFullYear(),
        month: this.dateEntered.getMonth() + 1,
        day: this.dateEntered.getDate()
      },
      enteredBy: this.enteredBy,
      agencyId: this.agencyId,
      agencyName: this.agencyName,
      lastName: this.lastName.value,
      firstName: this.firstName.value,
      middleInitial: this.middleInitial.value ? this.middleInitial.value : "",
      nameSuffix: this.nameSuffix.value ? this.nameSuffix.value : "",
      aliases: this.aliases,
      genderCode: this.gender.value,
      gender: gender.name,
      raceId: this.race.value,
      race: race.name,
      dateOfBirth: this.dateOfBirth.value,
      ssn: this.ssn.value ? this.ssn.value : "",
      sid: this.sid.value && this.sid.value !== "VA" ? this.sid.value : "",
      sourceId: this.reasonForCollection.value,
      reasonForCollection: sampleSource.preLogName,
      qualifyingCharge: this.getQualifyingCharge(),
      qcDropdown: this.qualifyingChargeDropdown.value ? this.qualifyingChargeDropdown.value : 0,
      qcTextbox: this.qualifyingChargeTextbox.value ? this.qualifyingChargeTextbox.value: "",
      qcDropdown2: this.qualifyingChargeDropdown2.value ? this.qualifyingChargeDropdown2.value : 0,
      qcFreeText: this.qualifyingChargeFreeText.value ? this.qualifyingChargeFreeText.value : "",
      state: this.state.value ? this.state.value : "",
      arrestingOfficer: this.arrestingOfficer.value ? this.arrestingOfficer.value : "",
      dcn: this.dcn.value ? this.dcn.value : "",
      docNumber: this.docNumber.value,
      collectedBy: this.collectedBy.value,
      dateCollected: this.dateCollected.value
    };
  }

  private getQualifyingCharge(): string {
    if (this.useOffenseDropdown) {
      let qualifyingCharge: IQualifyingOffense = this.qualifyingOffenses.find(x => x.id === this.qualifyingChargeDropdown.value);

      if (this.showRelatedOffense) {
        let qualifyingCharge2: IQualifyingOffense = this.qualifyingOffenses.find(x => x.id === this.qualifyingChargeDropdown2.value);
        return qualifyingCharge.code + " " + qualifyingCharge2.code;
      } else if (this.showOffenseFreeText) {
        return this.qualifyingChargeFreeText.value;
      } else {
        return qualifyingCharge.code
      }
    } else {
      return this.qualifyingChargeTextbox.value;
    }
  }

  private ConvertToNgbStruct(date: Date): NgbDateStruct {
    return date ? { year: date.getFullYear(), month: date.getMonth() + 1, day: date.getDate() } : null;
  }
}
